import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { db } from '@/lib/firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  or,
  Timestamp,
  getDocs
} from 'firebase/firestore';

export interface CallHistoryItem {
  id: string;
  contact: {
    id: string;
    name: string;
    avatar: string;
  };
  type: 'audio' | 'video';
  direction: 'incoming' | 'outgoing' | 'missed';
  duration: number; // in seconds, 0 for missed calls
  timestamp: Date;
  status: 'completed' | 'missed' | 'rejected' | 'failed';
}

export interface ContactCallInsight {
  contactId: string;
  name: string;
  avatar: string;
  totalCalls: number;
  completedCount: number;
  totalDuration: number;
  missedCount: number;
  lastCallAt: Date;
  latestMissedAt?: Date;
  latestCompletedAt?: Date;
}

export interface ContactReliabilityInsight {
  contactId: string;
  name: string;
  avatar: string;
  reliabilityScore: number;
  reconnectRiskScore: number;
  responseRate: number;
  nextBestHour: number | null;
  completedCount: number;
  missedCount: number;
}

export interface FollowUpInsight {
  contactId: string;
  name: string;
  avatar: string;
  missedCount: number;
  lastMissedAt: Date;
  priorityScore: number;
}

export interface CallHistoryInsights {
  totalCalls: number;
  completedCalls: number;
  missedCalls: number;
  outgoingCalls: number;
  incomingCalls: number;
  thisWeekCalls: number;
  previousWeekCalls: number;
  weeklyTrendPercent: number;
  totalDuration: number;
  averageDuration: number;
  bestCallHour: number | null;
  hourlyActivity: Array<{ hour: number; total: number; completed: number }>;
  relationshipHealthScore: number;
  topContacts: ContactCallInsight[];
  missedCallbacks: ContactCallInsight[];
  highPriorityFollowUps: FollowUpInsight[];
  reliabilityContacts: ContactReliabilityInsight[];
}

export function useCallHistory() {
  const { user } = useAuth();
  const [callHistory, setCallHistory] = useState<CallHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.uid) {
      setCallHistory([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const callsRef = collection(db, 'calls');
      
      // Query for calls where user is the caller
      const callerQuery = query(
        callsRef,
        where('callerId', '==', user.uid),
        orderBy('startTime', 'desc')
      );
      
      // Query for calls where user is the receiver
      const receiverQuery = query(
        callsRef,
        where('receiverId', '==', user.uid),
        orderBy('startTime', 'desc')
      );

      const processCallData = (doc: any) => {
        const data = doc.data();
        
        // Determine if this is an incoming or outgoing call
        const isIncoming = data.receiverId === user.uid;
        const isOutgoing = data.callerId === user.uid;
        
        // Get the contact (the other person in the call)
        // Handle both old format (callerName/receiverName) and new format (caller/receiver objects)
        let contact;
        if (isIncoming) {
          if (data.caller) {
            contact = data.caller;
          } else {
            contact = {
              id: data.callerId || '',
              name: data.callerName || 'Unknown',
              avatar: data.callerAvatar || `https://api.dicebear.com/7.x/initials/svg?seed=${data.callerId || 'unknown'}`
            };
          }
        } else {
          if (data.receiver) {
            contact = data.receiver;
          } else {
            contact = {
              id: data.receiverId || '',
              name: data.receiverName || 'Unknown',
              avatar: data.receiverAvatar || `https://api.dicebear.com/7.x/initials/svg?seed=${data.receiverId || 'unknown'}`
            };
          }
        }
        
        // Determine direction and status
        let direction: 'incoming' | 'outgoing' | 'missed' = isIncoming ? 'incoming' : 'outgoing';
        let status: 'completed' | 'missed' | 'rejected' | 'failed' = 'completed';
        
        if (data.status === 'rejected') {
          status = 'rejected';
        } else if (data.status === 'missed') {
          status = 'missed';
          direction = 'missed';
        } else if (data.status === 'ended' || data.status === 'connected') {
          status = 'completed';
        } else if (data.status === 'ringing') {
          // Skip calls that are still ringing
          return null;
        }
        
        // Calculate duration
        let duration = 0;
        if (data.duration) {
          // Use stored duration if available
          duration = data.duration;
        } else if (data.startTime && data.endTime && status === 'completed') {
          // Calculate from timestamps
          const start = data.startTime instanceof Timestamp ? data.startTime.toDate() : new Date(data.startTime);
          const end = data.endTime instanceof Timestamp ? data.endTime.toDate() : new Date(data.endTime);
          duration = Math.floor((end.getTime() - start.getTime()) / 1000);
        }
        
        // Convert timestamp - prefer startTime over createdAt
        let timestamp = new Date();
        if (data.startTime) {
          timestamp = data.startTime instanceof Timestamp 
            ? data.startTime.toDate() 
            : new Date(data.startTime);
        } else if (data.createdAt) {
          timestamp = data.createdAt instanceof Timestamp 
            ? data.createdAt.toDate() 
            : new Date(data.createdAt);
        }
        
        return {
          id: doc.id,
          contact: {
            id: contact?.id || '',
            name: contact?.name || 'Unknown',
            avatar: contact?.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${contact?.id || 'unknown'}`,
          },
          type: data.type || 'audio',
          direction,
          duration,
          timestamp,
          status,
        };
      };

      // Set up listeners for both queries
      const unsubscribeCaller = onSnapshot(
        callerQuery,
        (snapshot) => {
          const calls: CallHistoryItem[] = [];
          snapshot.forEach((doc) => {
            const call = processCallData(doc);
            if (call) calls.push(call);
          });
          
          // Merge with receiver calls and sort
          setCallHistory(prevHistory => {
            const receiverCalls = prevHistory.filter(call => 
              snapshot.docs.every(doc => doc.id !== call.id)
            );
            const merged = [...calls, ...receiverCalls];
            merged.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
            return merged;
          });
          
          setLoading(false);
        },
        (err) => {
          console.error('Error fetching caller history:', err);
          setError(err.message);
          setLoading(false);
        }
      );

      const unsubscribeReceiver = onSnapshot(
        receiverQuery,
        (snapshot) => {
          const calls: CallHistoryItem[] = [];
          snapshot.forEach((doc) => {
            const call = processCallData(doc);
            if (call) calls.push(call);
          });
          
          // Merge with caller calls and sort
          setCallHistory(prevHistory => {
            const callerCalls = prevHistory.filter(call => 
              snapshot.docs.every(doc => doc.id !== call.id)
            );
            const merged = [...calls, ...callerCalls];
            merged.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
            return merged;
          });
          
          setLoading(false);
        },
        (err) => {
          console.error('Error fetching receiver history:', err);
          setError(err.message);
          setLoading(false);
        }
      );

      return () => {
        unsubscribeCaller();
        unsubscribeReceiver();
      };
    } catch (err: any) {
      console.error('Error setting up call history listener:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [user?.uid]);

  const insights = useMemo<CallHistoryInsights>(() => {
    if (!callHistory.length) {
      return {
        totalCalls: 0,
        completedCalls: 0,
        missedCalls: 0,
        outgoingCalls: 0,
        incomingCalls: 0,
        thisWeekCalls: 0,
        previousWeekCalls: 0,
        weeklyTrendPercent: 0,
        totalDuration: 0,
        averageDuration: 0,
        bestCallHour: null,
        hourlyActivity: [],
        relationshipHealthScore: 0,
        topContacts: [],
        missedCallbacks: [],
        highPriorityFollowUps: [],
        reliabilityContacts: [],
      };
    }

    const contactMap = new Map<string, ContactCallInsight>();
    const hourlyMap = new Map<number, { hour: number; total: number; completed: number }>();
    const contactDayActivity = new Map<string, Set<string>>();
    const contactHourActivity = new Map<string, Map<number, { total: number; completed: number }>>();

    callHistory.forEach((call) => {
      const existing = contactMap.get(call.contact.id);
      const base: ContactCallInsight = existing || {
        contactId: call.contact.id,
        name: call.contact.name,
        avatar: call.contact.avatar,
        totalCalls: 0,
        completedCount: 0,
        totalDuration: 0,
        missedCount: 0,
        lastCallAt: call.timestamp,
        latestMissedAt: undefined,
        latestCompletedAt: undefined,
      };

      base.totalCalls += 1;
      base.totalDuration += Math.max(0, call.duration || 0);
      if (call.status === 'completed') {
        base.completedCount += 1;
      }
      if (call.status === 'missed') {
        base.missedCount += 1;
        if (!base.latestMissedAt || call.timestamp > base.latestMissedAt) {
          base.latestMissedAt = call.timestamp;
        }
      }
      if (call.status === 'completed') {
        if (!base.latestCompletedAt || call.timestamp > base.latestCompletedAt) {
          base.latestCompletedAt = call.timestamp;
        }
      }
      if (call.timestamp > base.lastCallAt) {
        base.lastCallAt = call.timestamp;
      }

      contactMap.set(call.contact.id, base);

      const dayKey = call.timestamp.toISOString().slice(0, 10);
      const daySet = contactDayActivity.get(call.contact.id) || new Set<string>();
      daySet.add(dayKey);
      contactDayActivity.set(call.contact.id, daySet);

      const contactHour = call.timestamp.getHours();
      const hourMap = contactHourActivity.get(call.contact.id) || new Map<number, { total: number; completed: number }>();
      const hourBucket = hourMap.get(contactHour) || { total: 0, completed: 0 };
      hourBucket.total += 1;
      if (call.status === 'completed') {
        hourBucket.completed += 1;
      }
      hourMap.set(contactHour, hourBucket);
      contactHourActivity.set(call.contact.id, hourMap);

      const hour = call.timestamp.getHours();
      const hourlyBucket = hourlyMap.get(hour) || { hour, total: 0, completed: 0 };
      hourlyBucket.total += 1;
      if (call.status === 'completed') {
        hourlyBucket.completed += 1;
      }
      hourlyMap.set(hour, hourlyBucket);
    });

    const totalDuration = callHistory.reduce((sum, call) => sum + Math.max(0, call.duration || 0), 0);
    const completedCalls = callHistory.filter((call) => call.status === 'completed').length;
    const missedCalls = callHistory.filter((call) => call.status === 'missed').length;
    const outgoingCalls = callHistory.filter((call) => call.direction === 'outgoing').length;
    const incomingCalls = callHistory.filter((call) => call.direction === 'incoming').length;
    const now = Date.now();
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const thisWeekStart = now - weekMs;
    const previousWeekStart = now - weekMs * 2;
    const thisWeekCalls = callHistory.filter((call) => call.timestamp.getTime() >= thisWeekStart).length;
    const previousWeekCalls = callHistory.filter((call) => {
      const ts = call.timestamp.getTime();
      return ts >= previousWeekStart && ts < thisWeekStart;
    }).length;
    const weeklyTrendPercent = previousWeekCalls === 0
      ? (thisWeekCalls > 0 ? 100 : 0)
      : Math.round(((thisWeekCalls - previousWeekCalls) / previousWeekCalls) * 100);
    const topContacts = Array.from(contactMap.values())
      .sort((a, b) => {
        if (b.totalCalls !== a.totalCalls) {
          return b.totalCalls - a.totalCalls;
        }
        return b.lastCallAt.getTime() - a.lastCallAt.getTime();
      })
      .slice(0, 5);

    const missedCallbacks = Array.from(contactMap.values())
      .filter((contact) => contact.missedCount > 0)
      .sort((a, b) => {
        if (b.missedCount !== a.missedCount) {
          return b.missedCount - a.missedCount;
        }
        return b.lastCallAt.getTime() - a.lastCallAt.getTime();
      })
      .slice(0, 5);

    const highPriorityFollowUps: FollowUpInsight[] = Array.from(contactMap.values())
      .filter((contact) => {
        if (!contact.latestMissedAt) {
          return false;
        }
        if (!contact.latestCompletedAt) {
          return true;
        }
        return contact.latestCompletedAt.getTime() < contact.latestMissedAt.getTime();
      })
      .map((contact) => {
        const lastMissedAt = contact.latestMissedAt as Date;
        const recencyHours = Math.max(1, (now - lastMissedAt.getTime()) / (1000 * 60 * 60));
        const priorityScore = Math.round((contact.missedCount * 12) + Math.max(0, 36 - recencyHours));
        return {
          contactId: contact.contactId,
          name: contact.name,
          avatar: contact.avatar,
          missedCount: contact.missedCount,
          lastMissedAt,
          priorityScore,
        };
      })
      .sort((a, b) => {
        if (b.priorityScore !== a.priorityScore) {
          return b.priorityScore - a.priorityScore;
        }
        return b.lastMissedAt.getTime() - a.lastMissedAt.getTime();
      })
      .slice(0, 5);

    const reliabilityContacts: ContactReliabilityInsight[] = Array.from(contactMap.values())
      .map((contact) => {
        const responseRate = contact.totalCalls > 0 ? contact.completedCount / contact.totalCalls : 0;
        const missedRate = contact.totalCalls > 0 ? contact.missedCount / contact.totalCalls : 0;

        const activeDays = contactDayActivity.get(contact.contactId)?.size || 1;
        const consistency = Math.min(1, activeDays / Math.max(1, Math.ceil(contact.totalCalls / 2)));

        const reliabilityScore = Math.max(0, Math.min(100, Math.round(
          responseRate * 60 + (1 - missedRate) * 25 + consistency * 15
        )));

        const daysSinceLastCall = Math.max(0, (now - contact.lastCallAt.getTime()) / (1000 * 60 * 60 * 24));
        const reconnectRiskScore = Math.max(0, Math.min(100, Math.round(
          missedRate * 60 + Math.min(25, daysSinceLastCall * 1.2) + (responseRate < 0.5 ? 15 : 0)
        )));

        const perHour = contactHourActivity.get(contact.contactId);
        const nextBestHour = perHour && perHour.size > 0
          ? Array.from(perHour.entries())
              .sort((a, b) => {
                const aRate = a[1].total > 0 ? a[1].completed / a[1].total : 0;
                const bRate = b[1].total > 0 ? b[1].completed / b[1].total : 0;
                if (bRate !== aRate) {
                  return bRate - aRate;
                }
                return b[1].completed - a[1].completed;
              })[0]?.[0] ?? null
          : null;

        return {
          contactId: contact.contactId,
          name: contact.name,
          avatar: contact.avatar,
          reliabilityScore,
          reconnectRiskScore,
          responseRate: Math.round(responseRate * 100),
          nextBestHour,
          completedCount: contact.completedCount,
          missedCount: contact.missedCount,
        };
      })
      .sort((a, b) => {
        if (b.reliabilityScore !== a.reliabilityScore) {
          return b.reliabilityScore - a.reliabilityScore;
        }
        return a.reconnectRiskScore - b.reconnectRiskScore;
      })
      .slice(0, 6);

    const relationshipHealthScore = reliabilityContacts.length > 0
      ? Math.max(0, Math.min(100, Math.round(
          reliabilityContacts.reduce((sum, contact) => sum + (contact.reliabilityScore - contact.reconnectRiskScore * 0.35), 0)
          / reliabilityContacts.length
        )))
      : 0;

    const hourlyActivity = Array.from(hourlyMap.values()).sort((a, b) => a.hour - b.hour);
    const bestCallHour = hourlyActivity.length > 0
      ? [...hourlyActivity]
          .sort((a, b) => {
            const aRate = a.total > 0 ? a.completed / a.total : 0;
            const bRate = b.total > 0 ? b.completed / b.total : 0;
            if (bRate !== aRate) {
              return bRate - aRate;
            }
            return b.completed - a.completed;
          })[0]?.hour ?? null
      : null;

    return {
      totalCalls: callHistory.length,
      completedCalls,
      missedCalls,
      outgoingCalls,
      incomingCalls,
      thisWeekCalls,
      previousWeekCalls,
      weeklyTrendPercent,
      totalDuration,
      averageDuration: callHistory.length > 0 ? Math.round(totalDuration / callHistory.length) : 0,
      bestCallHour,
      hourlyActivity,
      relationshipHealthScore,
      topContacts,
      missedCallbacks,
      highPriorityFollowUps,
      reliabilityContacts,
    };
  }, [callHistory]);

  return { callHistory, loading, error, insights };
}
